import { untracked } from "@suse-o11y/signal";
import React from "react";

import { Button } from "~/components/button";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormFieldReset } from "~/components/form/reset";
import { isPresent } from "~/tools/is-present";

import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";

import { OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION } from "./_built-in-variables";
import { BuiltInVariablesTable } from "./_built-in-variables-table";
import type { DrawerState } from "./_drawer-state";
import { FormContextProvider, type FormContextValue } from "./_form-context";
import { FORM_ID } from "./_form-id";
import { VariablesTable } from "./_variables-table";

export type DrawerContentProps = DrawerState;

export const DrawerContent: React.FC<DrawerContentProps> = ({ timeRange, variables, id, onSave }) => {
	const explorationDrawer = useExplorationDrawerState();
	const context = React.useMemo<FormContextValue>(() => ({ timeRange }), [timeRange]);

	return (
		<FormContextProvider value={context}>
			<DashboardSaveAlert className="mb-4" placement="dashboard-variables-drawer" />

			<Form
				id={FORM_ID}
				onSubmit={(output) => {
					onSave(output);
				}}
				state={variables}
			>
				<FormField className="items-stretch" state={variables}>
					<VariablesTable id={id} variables={variables} />

					<div className="mt-4 flex items-end justify-end gap-4">
						<FormFieldReset asChild>
							<Button className="uppercase" variant="secondary">
								Reset variables
							</Button>
						</FormFieldReset>
						<Button
							className="uppercase"
							onClick={() => {
								untracked((monitor) => {
									explorationDrawer.openCreateDashboardVariable({
										id: `${id}_add_variable`,
										timeRange,
										// pass only valid variables
										variables: variables.getOutput(monitor)?.filter(isPresent) ?? [],
										onSave: (variable) => {
											variables.setInput((current) => [...current, variable]);
										},
									});
								});
							}}
							variant="primary"
						>
							Add variable
						</Button>
					</div>
				</FormField>
				<div className="mt-4">
					<BuiltInVariablesTable
						title="SUSE Observability PromQL variables"
						variables={OBSERVABILITY_PROMQL_VARIABLES.map((name) => ({
							name,
							description: OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION[name],
						}))}
					/>
				</div>
			</Form>
		</FormContextProvider>
	);
};
